import { createAsyncThunk, createEntityAdapter, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { CmtsReduxThunkExtra, CmtsSerializableError } from "./thunkExtra.js";
import { readReduxString, serializeReduxError, serializeRepositoryFailure } from "./thunkExtra.js";

export type CmtsComplaintSummaryEntity = {
  readonly id: string;
  readonly complaintNumber?: string | undefined;
  readonly title?: string | undefined;
  readonly status?: string | undefined;
  readonly priority?: string | undefined;
  readonly severity?: string | undefined;
  readonly tenantCode?: string | undefined;
  readonly updatedAtUtc?: string | undefined;
};

const complaintAdapter = createEntityAdapter<CmtsComplaintSummaryEntity>();

export type FetchCmtsComplaintAllowedActionsArgs = { readonly complaintId: string; readonly tenantId?: string | undefined };

function workflowActionCode(action: unknown): string {
  return readReduxString(action, ["actionCode", "code", "id", "name"], "");
}

export const fetchCmtsComplaintAllowedActions = createAsyncThunk<{ readonly complaintId: string; readonly actions: readonly string[] }, FetchCmtsComplaintAllowedActionsArgs, { extra: CmtsReduxThunkExtra; rejectValue: CmtsSerializableError }>(
  "complaint/fetchAllowedActions",
  async (args, thunkApi) => {
    try {
      if (!thunkApi.extra.repositories) {
        return thunkApi.rejectWithValue({ message: "Repository suite is not available for complaint allowed-action refresh.", atUtc: new Date().toISOString() });
      }
      const result = await thunkApi.extra.repositories.complaints.allowedActions(args.complaintId, { tenantId: args.tenantId });
      if (!result.ok) {
        return thunkApi.rejectWithValue(serializeRepositoryFailure(result.diagnostics, "Complaint allowed-action refresh failed."));
      }
      return { complaintId: args.complaintId, actions: result.data.map(workflowActionCode).filter(Boolean) };
    } catch (error: unknown) {
      return thunkApi.rejectWithValue(serializeReduxError(error, "Complaint allowed-action refresh failed."));
    }
  },
);


export type CmtsComplaintState = {
  readonly complaints: ReturnType<typeof complaintAdapter.getInitialState>;
  readonly selectedComplaintId?: string | undefined;
  readonly allowedActions: readonly string[];
  readonly pageLoading: boolean;
  readonly workflowActionLoading: boolean;
  readonly commentsLoading: boolean;
  readonly tasksLoading: boolean;
  readonly evidenceLoading: boolean;
  readonly staleConflictOrDependencyError?: CmtsSerializableError | undefined;
  readonly error?: CmtsSerializableError | undefined;
  readonly lastLoadedAtUtc?: string | undefined;
};

const initialState: CmtsComplaintState = {
  complaints: complaintAdapter.getInitialState(),
  allowedActions: [],
  pageLoading: false,
  workflowActionLoading: false,
  commentsLoading: false,
  tasksLoading: false,
  evidenceLoading: false,
};

const complaintSlice = createSlice({
  name: "complaint",
  initialState,
  reducers: {
    complaintListLoadStarted(state) {
      state.pageLoading = true;
      state.error = undefined;
    },
    complaintListLoaded(state, action: PayloadAction<readonly CmtsComplaintSummaryEntity[]>) {
      complaintAdapter.setAll(state.complaints, action.payload);
      state.pageLoading = false;
      state.error = undefined;
      state.lastLoadedAtUtc = new Date().toISOString();
    },
    complaintSummaryUpserted(state, action: PayloadAction<CmtsComplaintSummaryEntity>) {
      complaintAdapter.upsertOne(state.complaints, action.payload);
      state.lastLoadedAtUtc = new Date().toISOString();
    },
    complaintSelected(state, action: PayloadAction<string | undefined>) {
      state.selectedComplaintId = action.payload;
      state.allowedActions = [];
      state.error = undefined;
    },
    complaintAllowedActionsLoaded(state, action: PayloadAction<{ complaintId: string; actions: readonly string[] }>) {
      state.selectedComplaintId = action.payload.complaintId;
      state.allowedActions = [...action.payload.actions];
      state.workflowActionLoading = false;
      state.error = undefined;
    },
    complaintWorkflowActionStarted(state) {
      state.workflowActionLoading = true;
      state.error = undefined;
    },
    complaintWorkflowActionCompleted(state) {
      state.workflowActionLoading = false;
    },
    complaintErrorRecorded(state, action: PayloadAction<CmtsSerializableError>) {
      state.pageLoading = false;
      state.workflowActionLoading = false;
      state.error = action.payload;
    },
    complaintConflictRecorded(state, action: PayloadAction<CmtsSerializableError>) {
      state.staleConflictOrDependencyError = action.payload;
      state.error = action.payload;
    },
    complaintCleared(state) {
      complaintAdapter.removeAll(state.complaints);
      state.selectedComplaintId = undefined;
      state.allowedActions = [];
      state.pageLoading = false;
      state.workflowActionLoading = false;
      state.commentsLoading = false;
      state.tasksLoading = false;
      state.evidenceLoading = false;
      state.staleConflictOrDependencyError = undefined;
      state.error = undefined;
      state.lastLoadedAtUtc = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCmtsComplaintAllowedActions.pending, (state, action) => {
        state.selectedComplaintId = action.meta.arg.complaintId;
        state.workflowActionLoading = true;
        state.error = undefined;
      })
      .addCase(fetchCmtsComplaintAllowedActions.fulfilled, (state, action) => {
        state.selectedComplaintId = action.payload.complaintId;
        state.allowedActions = [...action.payload.actions];
        state.workflowActionLoading = false;
        state.error = undefined;
      })
      .addCase(fetchCmtsComplaintAllowedActions.rejected, (state, action) => {
        state.selectedComplaintId = action.meta.arg.complaintId;
        state.workflowActionLoading = false;
        state.error = action.payload ?? serializeReduxError(action.error, "Complaint allowed-action refresh failed.");
      });
  },
});

export const complaintEntityAdapter = complaintAdapter;
export const complaintActions = complaintSlice.actions;
export const complaintReducer = complaintSlice.reducer;
